<template>
    <form-wrapper class="practice-information-ancillary-form" @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.practice.account_information.fields.practice_name.label')"
                    :placeholder="$t('forms.practice.account_information.fields.practice_name.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="name"
                    @change="change($event), (isSaveDisabled = false)"
                ></text-field>
            </form-cell>
            <form-cell width="half">
                <common-content-spacer height="small">
                    <phone-field
                        :label="$t('forms.profile.account_information.fields.mobile_phone.label')"
                        :placeholder="$t('forms.profile.account_information.fields.mobile_phone.placeholder')"
                        :isRequired="false"
                        :formData="data"
                        valuePath="mobilePhone"
                        @change="change($event)"
                    ></phone-field>
                    <checkbox-field :formData="data" valuePath="isAgreedToSms" @change="change($event)">
                        <template #label>
                            <span>
                                {{ $t('forms.profile.account_information.fields.agree_to_sms.label') }}
                            </span>
                        </template>
                    </checkbox-field>
                </common-content-spacer>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.practice.account_information.fields.phone.label')"
                    :placeholder="$t('forms.practice.account_information.fields.phone.placeholder')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="phone"
                    @change="change($event), (isSaveDisabled = false)"
                ></text-field>
            </form-cell>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.practice.account_information.fields.fax_number.label')"
                    :placeholder="$t('forms.practice.account_information.fields.fax_number.placeholder')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="faxNumber"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.practice.account_information.fields.email.label')"
                    :isDisabled="true"
                    :formData="data"
                    valuePath="email"
                    @change="change($event)"
                ></text-field>
                <common-button
                    :isSubmittable="false"
                    :label="$t('forms.practice.account_information.buttons.change_email')"
                    type="link"
                    @click="openChangeEmailModal"
                ></common-button>
            </form-cell>
            <form-cell width="half">
                <form-field :label="$t('forms.practice.account_information.fields.password.label')">
                    **********
                    <common-button
                        :isSubmittable="false"
                        :label="$t('forms.practice.account_information.buttons.modify_password')"
                        type="link"
                        @click="openChangePasswordModal"
                    ></common-button>
                </form-field>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell class="practice-ancillary">
                <common-content-spacer height="small">
                    <div class="practice-ancillary--header">
                        <common-icon name="insurance" size="large"></common-icon>
                        <common-page-header
                            size="page-plain-header"
                            :heading="$t('forms.practice_ancillary.fields.insurance')"
                        ></common-page-header>
                    </div>
                </common-content-spacer>
                <common-button
                    :isSubmittable="false"
                    :label="$t('forms.practice.account_information.buttons.modify_insurances')"
                    type="link"
                    :route="{ name: 'practice.insurance' }"
                ></common-button>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="four-fifths">
                <div class="practice-information-form__overflow-y">
                    <common-badges-list v-if="insurancePlans.length" :listElements="insurancePlans"></common-badges-list>
                </div>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell class="practice-ancillary">
                <common-content-spacer height="small">
                    <div class="practice-ancillary--header">
                        <common-icon name="ancillary-diagnostic" size="large"></common-icon>
                        <common-page-header
                            size="page-plain-header"
                            :heading="$t('forms.practice_ancillary.fields.services')"
                        ></common-page-header>
                    </div>
                </common-content-spacer>
                <common-button
                    :isSubmittable="false"
                    :label="$t('forms.practice_ancillary.button.modify')"
                    type="link"
                    :route="{ name: 'practice.services' }"
                ></common-button>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="four-fifths">
                <div class="practice-information-form__overflow-y">
                    <common-badges-link-list
                        v-if="ancillaryServices.length"
                        :listElements="ancillaryServices"
                    ></common-badges-link-list>
                </div>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell>
                <common-content-spacer size="medium" height="small">
                    <div class="practice-information-ancillary-form__location-container">
                        <div class="practice-ancillary--header">
                            <common-icon name="location" size="large"></common-icon>
                            <common-page-header
                                size="page-plain-header"
                                :heading="$t('forms.practice_ancillary.fields.locations')"
                            ></common-page-header>
                        </div>

                        <common-button
                            :isSubmittable="false"
                            :label="$t('forms.practice_ancillary.button.add_modify')"
                            type="link"
                            :route="{ name: 'practice.locations' }"
                        ></common-button>
                    </div>
                    <availability-locations
                        v-if="data.locations && data.locations.length"
                        :hoverTitle="$t('pages.profile.professional.availability.check')"
                        :maxNumberOfItems="3"
                        :locations="data.locations"
                        @go-to-location="goToModify($event)"
                    ></availability-locations>
                </common-content-spacer>
            </form-cell>
        </form-row>
        <template #footer>
            <common-buttons>
                <common-button
                    :isSubmittable="false"
                    :label="$t('pages.practice.profile.buttons.secondary')"
                    type="secondary"
                    @click="goToDashboard"
                ></common-button>
                <common-button
                    :isProcessing="isSending"
                    :isDisabled="isSaveDisabled"
                    :label="$t('pages.practice.profile.buttons.primary')"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>
        <change-password-modal
            :isOpen="isChangePasswordModalOpen"
            @close="closeChangePasswordModal"
            @password-changed="passwordChanged"
        ></change-password-modal>
        <change-practice-email-modal
            :isOpen="isChangeEmailModalOpen"
            @close="closeChangeEmailModal"
            @email-changed="updateEmail"
        ></change-practice-email-modal>
        <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import ChangePasswordModal from 'components/page-elements/profile/ChangePasswordModal'
    import ChangePracticeEmailModal from 'components/page-elements/practice/ChangePracticeEmailModal'
    import StatesAPI from 'api/StatesAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import AvailabilityLocations from 'components/page-elements/profile/AvailabilityLocations'
    import isequal from 'lodash.isequal'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [FormMixin, HandleRequestMixin],
        components: {
            ChangePasswordModal,
            ChangePracticeEmailModal,
            AvailabilityLocations,
        },

        data() {
            return {
                isChangeEmailModalOpen: false,
                isChangePasswordModalOpen: false,
                isChangeInsurancesModalOpen: false,
                insurancePlans: [],
                states: [],
                ancillaryServices: [],
                ancillaryTests: [],
                initialData: {},
                isSaveDisabled: true,
                notificationMessage: '',
            }
        },

        methods: {
            passwordChanged() {
                this.closeChangePasswordModal()
            },
            goToIndex() {
                this.$router.push({ name: 'practice.members' })
            },
            goToDashboard() {
                this.$router.push({ name: 'index.home' })
            },
            openChangeEmailModal() {
                this.isChangeEmailModalOpen = true
            },
            closeChangeEmailModal() {
                this.isChangeEmailModalOpen = false
            },
            openChangePasswordModal() {
                this.isChangePasswordModalOpen = true
            },
            closeChangePasswordModal() {
                this.isChangePasswordModalOpen = false
            },
            openChangeInsurancesModal() {
                this.isChangeInsurancesModalOpen = true
            },
            updateEmail(ev) {
                this.data.email = ev
                this.emitEvent({ name: 'form-changed', payload: !isequal(this.initialData, this.data) })
                this.closeChangeEmailModal()
                this.notificationMessage = this.$t('pages.practice.profile.email_saved')
                this.$refs.commonSuccessNotification.showNotification()
            },
            goToModify(locationObject) {
                const query = {
                    locationId: locationObject['practiceLocationId'],
                }

                const params = {
                    action: 'schedule',
                }

                this.$router.push({ name: 'availability', params, query })
            },
            loadData() {
                StatesAPI.getList().then((res) => (this.states = res))
                const practice = this.$auth.getPractice()
                this.ancillaryServices = practice.facilityServiceTypes.map((item) => ({
                    name: item.name,
                    redirectRoute: `/practice/service/${item.id}/tests`,
                }))
                this.insurancePlans = practice.insurancePlans.map((plan) => plan.name)

                const mainLocation = {
                    address: practice.address,
                    city: practice.city,
                    state: practice.state,
                    zip: practice.zip,
                    isAvailabilitySet: practice.isAvailabilitySet,
                }
                this.data = {
                    id: practice.id,
                    name: practice.name,
                    email: practice.email,
                    password: practice.password,
                    phone: practice.phone ? practice.phone : null,
                    mobilePhone: practice.mobilePhone,
                    isAgreedToSms: practice.isAgreedToSms,
                    faxNumber: practice.fax,
                    locations: [mainLocation, ...practice.locations],
                }
                this.initialData = cloneDeep(this.data)
            },
            handleFormSubmission(data) {
                this.isSending = true
                this.emitEvent({ name: 'is-sending', payload: this.isSending })
                return this.handleRequest(PracticesAPI.update(this.data.id, data)).then(() => {
                    this.goToDashboard()
                })
            },
        },
        mounted() {
            this.loadData()
            this.$on('local:afterChange', () => {
                this.emitEvent({ name: 'form-changed', payload: !isequal(this.initialData, this.data) })
            })
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/ancillary-practice-profile/_ancillary-practice-profile.scss';
</style>
